import React, { useState, useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Link, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import ShutterQuote from "./components/ShutterQuote";
import PricingSelection from "./components/PricingSelection";
import AdminPanel from "./components/AdminPanel";
import LoginForm from "./components/auth/LoginForm";
import RegisterForm from "./components/auth/RegisterForm";
import AccountDashboard from "./components/account/AccountDashboard";
import AccountSettings from "./components/account/AccountSettings";
import Homepage from "./components/website/Homepage";
import MaterialPage from "./components/website/MaterialPage";
import RoomsPage from "./components/website/RoomsPage";
import ShutterStylesPage from "./components/website/ShutterStylesPage";
import LouvreSizesPage from "./components/website/LouvreSizesPage";
import AboutPage from "./components/website/AboutPage";
import ContactPage from "./components/website/ContactPage";
import PrivacyPolicy from "./components/website/PrivacyPolicy";
import TermsOfService from "./components/website/TermsOfService";
import ShuttersMainPage from "./components/website/ShuttersMainPage";
import PaymentConfirmation from "./pages/PaymentConfirmation";
import QuoteSelectionPage from "./pages/QuoteSelectionPage";
import UnsubscribePage from "./pages/UnsubscribePage";
import LandingPage from "./pages/LandingPage";
import BookHomeVisitPage from "./pages/BookHomeVisitPage";
import RequestHomeVisitPage from "./pages/RequestHomeVisitPage";
import DirectHomeVisitPage from "./pages/DirectHomeVisitPage";
// Shutter Range Pages
import BudgetPage from "./pages/ranges/BudgetPage";
import PolymerPage from "./pages/ranges/PolymerPage";
import PaulowniaPage from "./pages/ranges/PaulowniaPage";
import BasswoodPage from "./pages/ranges/BasswoodPage";
import UKBasswoodPage from "./pages/ranges/UKBasswoodPage";
import AluminiumPage from "./pages/ranges/AluminiumPage";
// All door-related components removed - focusing solely on shutters
import FittedWardrobesPage from "./components/fitted-furniture/FittedWardrobesPage";
import FittedSlidingWardrobesPage from "./components/fitted-furniture/FittedSlidingWardrobesPage";
import FittedBedroomFurniturePage from "./components/fitted-furniture/FittedBedroomFurniturePage";
import WalkInWardrobesPage from "./components/fitted-furniture/WalkInWardrobesPage";
import FittedLivingRoomsPage from "./components/fitted-furniture/FittedLivingRoomsPage";
import FittedMediaWallsPage from "./components/fitted-furniture/FittedMediaWallsPage";
import FittedHomeOfficePage from "./components/fitted-furniture/FittedHomeOfficePage";
// All door-related component imports removed - focusing solely on shutters
import Footer from "./components/Footer";
import ScrollToTop from "./components/ScrollToTop";
import { Toaster } from "./components/ui/toaster";
import { Button } from "./components/ui/button";
import { Users, Calculator, User, LogIn, Home, ChevronDown, Menu, X, Info, Phone } from "lucide-react";
import { authApi } from "./services/authApi";

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AppContent />
      </BrowserRouter>
      <Toaster />
    </div>
  );
}

// Separate component to access useLocation inside BrowserRouter
function AppContent() {
  const location = useLocation();
  const isAuthenticated = authApi.isAuthenticated();
  const user = authApi.getStoredUser();
  const [openDropdown, setOpenDropdown] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Pages that should not show the main navbar/footer
  const isLandingPage = location.pathname === '/get-quote' || location.pathname === '/book-home-visit' || location.pathname === '/request-home-visit' || location.pathname === '/free-home-visit';

  const shutterLinks = [
    { to: "/styles", label: "Shutter Styles" },
    { to: "/materials", label: "Materials" },
    { to: "/louvre-sizes", label: "Louvre Sizes" },
    { to: "/rooms", label: "Rooms" }
  ];

  const shutterRangesLinks = [
    { to: "/ranges/budget", label: "Budget Shutters" },
    { to: "/ranges/polymer", label: "Polymer Shutters" },
    { to: "/ranges/paulownia", label: "Paulownia+ Shutters" },
    { to: "/ranges/basswood", label: "Basswood Shutters" },
    { to: "/ranges/uk-basswood", label: "UK Made Basswood" },
    { to: "/ranges/aluminium", label: "Aluminium Shutters" }
  ];

  // All door links removed - focusing solely on shutters

  // Fitted Furniture links - TEMPORARILY HIDDEN but kept for easy restoration
  const fittedFurnitureLinks = [
    { to: "/fitted-furniture/wardrobes", label: "Fitted Wardrobes" },
    { to: "/fitted-furniture/sliding-wardrobes", label: "Fitted Sliding Wardrobes" },
    { to: "/fitted-furniture/bedroom-furniture", label: "Fitted Bedroom Furniture" },
    { to: "/fitted-furniture/walk-in-wardrobes", label: "Walk In Wardrobes" },
    { to: "/fitted-furniture/living-rooms", label: "Fitted Living Rooms" },
    { to: "/fitted-furniture/media-walls", label: "Fitted Media Walls" },
    { to: "/fitted-furniture/home-office", label: "Fitted Home Office" }
  ];

  const toggleDropdown = (dropdown) => {
    setOpenDropdown(openDropdown === dropdown ? null : dropdown);
  };

  const closeDropdown = () => {
    setOpenDropdown(null);
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
    setOpenDropdown(null);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
    setOpenDropdown(null);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.dropdown-container') && !event.target.closest('.mobile-menu')) {
        setOpenDropdown(null);
      }
    };

    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setMobileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('resize', handleResize);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <>
      <ScrollToTop />
      {/* Navigation - Hidden on landing page */}
      {!isLandingPage && (
        <nav className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-md border-b border-stone-200">
          <div className="max-w-7xl mx-auto px-6 lg:px-8 py-4 flex justify-between items-center">
            {/* Logo and Desktop Navigation */}
            <div className="flex items-center gap-8">
              <Link to="/" className="flex items-center">
                <img 
                  src="https://customer-assets.emergentagent.com/job_window-quotes-1/artifacts/kzwoymfj_Aston%20Lily%20Shutters%20Web%20Banner%20.png" 
                  alt="Aston Lily Shutters" 
                  className="h-16 md:h-20 lg:h-24"
                />
              </Link>
              
              {/* Desktop Navigation */}
              <div className="hidden md:flex items-center gap-1 relative">
                <Link to="/about">
                  <Button variant="ghost" size="sm" className="text-slate-850 hover:text-sage-600 hover:bg-sage-50 rounded-full px-4">About</Button>
                </Link>
                
                {/* Shutters Dropdown */}
                <div className="relative dropdown-container">
                  <div className="flex items-center">
                    <Link to="/shutters" className="hover:no-underline">
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="text-slate-850 hover:text-sage-600 hover:bg-sage-50 rounded-full rounded-r-none border-r-0 px-4"
                      >
                        Shutters
                      </Button>
                    </Link>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="text-slate-850 hover:text-sage-600 hover:bg-sage-50 px-2 rounded-full rounded-l-none"
                      onClick={() => toggleDropdown('shutters')}
                    >
                      <ChevronDown className="h-4 w-4" />
                    </Button>
                  </div>
                  {openDropdown === 'shutters' && (
                    <div className="absolute top-full left-0 mt-2 w-52 bg-white border border-stone-200 shadow-lift rounded-xl z-50 overflow-hidden">
                      {shutterLinks.map((link) => (
                        <Link key={link.to} to={link.to} onClick={closeDropdown}>
                          <div className="px-4 py-3 hover:bg-sage-50 cursor-pointer text-sm text-slate-850 transition-colors">
                            {link.label}
                          </div>
                        </Link>
                      ))}
                    </div>
                  )}
                </div>

                {/* All door-related navigation removed - focusing solely on shutters */}

                {/* Our Shutter Ranges Dropdown */}
                <div className="relative dropdown-container">
                  <div className="flex items-center">
                    <Link to="/materials" className="hover:no-underline">
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="text-slate-850 hover:text-sage-600 hover:bg-sage-50 rounded-full rounded-r-none border-r-0 px-4"
                      >
                        Our Shutter Ranges
                      </Button>
                    </Link>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="text-slate-850 hover:text-sage-600 hover:bg-sage-50 px-2 rounded-full rounded-l-none"
                      onClick={() => toggleDropdown('ranges')}
                    >
                      <ChevronDown className="h-4 w-4" />
                    </Button>
                  </div>
                  {openDropdown === 'ranges' && (
                    <div className="absolute top-full left-0 mt-2 w-52 bg-white border border-stone-200 shadow-lift rounded-xl z-50 overflow-hidden">
                      {shutterRangesLinks.map((link) => (
                        <Link key={link.to} to={link.to} onClick={closeDropdown}>
                          <div className="px-4 py-3 hover:bg-sage-50 cursor-pointer text-sm text-slate-850 transition-colors">
                            {link.label}
                          </div>
                        </Link>
                      ))}
                    </div>
                  )}
                </div>

                {/* Fitted Furniture Dropdown - TEMPORARILY HIDDEN 
                <div className="relative dropdown-container">
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="flex items-center gap-1"
                    onClick={() => toggleDropdown('fitted-furniture')}
                  >
                    Fitted Furniture
                    <ChevronDown className="h-4 w-4" />
                  </Button>
                  {openDropdown === 'fitted-furniture' && (
                    <div className="absolute top-full left-0 mt-1 w-56 bg-white border border-gray-200 shadow-lg rounded-md z-50">
                      {fittedFurnitureLinks.map((link) => (
                        <Link key={link.to} to={link.to} onClick={closeDropdown}>
                          <div className="px-4 py-2 hover:bg-gray-50 cursor-pointer text-sm">
                            {link.label}
                          </div>
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
                */}

                <Link to="/contact">
                  <Button variant="ghost" size="sm" className="text-slate-850 hover:text-sage-600 hover:bg-sage-50 rounded-full px-4">Contact</Button>
                </Link>
              </div>
            </div>

            {/* Desktop Right Side Navigation */}
            <div className="hidden md:flex items-center gap-3">
              <Link to="/calculator">
                <Button className="bg-sage-400 hover:bg-sage-500 text-white rounded-full px-5 py-2 text-sm font-medium">
                  Use Our Online Estimator
                </Button>
              </Link>
              {isAuthenticated ? (
                <>
                  <Link to="/account/dashboard">
                    <Button variant="ghost" size="sm" className="text-slate-850 hover:text-sage-600 hover:bg-sage-50 rounded-full flex items-center gap-2">
                      <User className="h-4 w-4" />
                      Account
                    </Button>
                  </Link>
                </>
              ) : (
                <>
                  <Link to="/account/login">
                    <Button variant="ghost" size="sm" className="text-slate-850 hover:text-sage-600 hover:bg-sage-50 rounded-full flex items-center gap-2">
                      <LogIn className="h-4 w-4" />
                      Sign In
                    </Button>
                  </Link>
                </>
              )}
              <Link to="/admin">
                <Button variant="outline" size="sm" className="text-slate-850 border-stone-300 hover:bg-sage-50 rounded-full flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  Admin
                </Button>
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <div className="md:hidden">
              <Button
                variant="ghost"
                size="sm"
                onClick={toggleMobileMenu}
                className="p-2"
              >
                {mobileMenuOpen ? (
                  <X className="h-6 w-6" />
                ) : (
                  <Menu className="h-6 w-6" />
                )}
              </Button>
            </div>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden mt-4 pb-4 mobile-menu">
              <div className="space-y-2">
                {/* Main Navigation */}
                <Link to="/about" onClick={closeMobileMenu}>
                  <Button variant="ghost" className="w-full justify-start text-left">
                    <Info className="h-4 w-4 mr-2" />
                    About Us
                  </Button>
                </Link>

                <Link to="/shutters" onClick={closeMobileMenu}>
                  <Button variant="ghost" className="w-full justify-start text-left">
                    <Home className="h-4 w-4 mr-2" />
                    Shutters
                  </Button>
                </Link>

                {/* Shutter Sub-menu */}
                <div className="pl-6 space-y-1">
                  {shutterLinks.map((link) => (
                    <Link key={link.to} to={link.to} onClick={closeMobileMenu}>
                      <Button variant="ghost" size="sm" className="w-full justify-start text-left text-sm text-gray-600">
                        {link.label}
                      </Button>
                    </Link>
                  ))}
                </div>

                {/* All door-related mobile navigation removed - focusing solely on shutters */}

                {/* Our Shutter Ranges */}
                <Link to="/materials" onClick={closeMobileMenu}>
                  <Button variant="ghost" className="w-full justify-start text-left">
                    <Home className="h-4 w-4 mr-2" />
                    Our Shutter Ranges
                  </Button>
                </Link>

                {/* Shutter Ranges Sub-menu */}
                <div className="pl-6 space-y-1">
                  {shutterRangesLinks.map((link) => (
                    <Link key={link.to} to={link.to} onClick={closeMobileMenu}>
                      <Button variant="ghost" size="sm" className="w-full justify-start text-left text-sm text-gray-600">
                        {link.label}
                      </Button>
                    </Link>
                  ))}
                </div>

                <Link to="/contact" onClick={closeMobileMenu}>
                  <Button variant="ghost" className="w-full justify-start text-left">
                    <Phone className="h-4 w-4 mr-2" />
                    Contact Us
                  </Button>
                </Link>

                {/* Divider */}
                <div className="border-t border-gray-200 my-4"></div>

                {/* Tools & Account */}
                <Link to="/calculator" onClick={closeMobileMenu}>
                  <Button variant="ghost" className="w-full justify-start text-left">
                    <Calculator className="h-4 w-4 mr-2" />
                    Pricing Calculator
                  </Button>
                </Link>

                {isAuthenticated ? (
                  <Link to="/account/dashboard" onClick={closeMobileMenu}>
                    <Button variant="ghost" className="w-full justify-start text-left">
                      <User className="h-4 w-4 mr-2" />
                      My Account
                    </Button>
                  </Link>
                ) : (
                  <Link to="/account/login" onClick={closeMobileMenu}>
                    <Button variant="ghost" className="w-full justify-start text-left">
                      <LogIn className="h-4 w-4 mr-2" />
                      Sign In
                    </Button>
                  </Link>
                )}

                <Link to="/admin" onClick={closeMobileMenu}>
                  <Button variant="ghost" className="w-full justify-start text-left">
                    <Users className="h-4 w-4 mr-2" />
                    Admin Panel
                  </Button>
                </Link>
              </div>
            </div>
          )}
        </nav>
      )}

      {/* Main content wrapper - no padding on landing page */}
      <main className={isLandingPage ? '' : 'pt-20 md:pt-24 lg:pt-28'}>
        <Routes>
          <Route path="/" element={<Homepage />} />
          <Route path="/calculator" element={<PricingSelection />} />
          <Route path="/calculator/shutters" element={<ShutterQuote />} />
          {/* All door routes removed - focusing solely on shutters */}
          <Route path="/shutters" element={<ShuttersMainPage />} />

          <Route path="/styles" element={<ShutterStylesPage />} />
          <Route path="/shutters/styles" element={<ShutterStylesPage />} />
          <Route path="/materials" element={<MaterialPage />} />
          <Route path="/shutters/materials" element={<MaterialPage />} />
          <Route path="/shutters/louvre-sizes" element={<LouvreSizesPage />} />
          <Route path="/louvre-sizes" element={<LouvreSizesPage />} />
          <Route path="/shutters/rooms" element={<RoomsPage />} />
          <Route path="/rooms" element={<RoomsPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/contact" element={<ContactPage />} />

          <Route path="/fitted-furniture/wardrobes" element={<FittedWardrobesPage />} />
          <Route path="/fitted-furniture/sliding-wardrobes" element={<FittedSlidingWardrobesPage />} />
          <Route path="/fitted-furniture/bedroom-furniture" element={<FittedBedroomFurniturePage />} />
          <Route path="/fitted-furniture/walk-in-wardrobes" element={<WalkInWardrobesPage />} />
          <Route path="/fitted-furniture/living-rooms" element={<FittedLivingRoomsPage />} />
          <Route path="/fitted-furniture/media-walls" element={<FittedMediaWallsPage />} />
          <Route path="/fitted-furniture/home-office" element={<FittedHomeOfficePage />} />
          <Route path="/admin" element={<AdminPanel />} />
          <Route path="/account/login" element={<LoginForm />} />
          <Route path="/account/register" element={<RegisterForm />} />
          <Route path="/account/dashboard" element={<AccountDashboard />} />
          <Route path="/account/settings" element={<AccountSettings />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/payment-confirmation" element={<PaymentConfirmation />} />
          <Route path="/quote/select/:token" element={<QuoteSelectionPage />} />
          <Route path="/unsubscribe/:token" element={<UnsubscribePage />} />
          {/* Shutter Range Pages */}
          <Route path="/ranges/budget" element={<BudgetPage />} />
          <Route path="/ranges/polymer" element={<PolymerPage />} />
          <Route path="/ranges/paulownia" element={<PaulowniaPage />} />
          <Route path="/ranges/basswood" element={<BasswoodPage />} />
          <Route path="/ranges/uk-basswood" element={<UKBasswoodPage />} />
          <Route path="/ranges/aluminium" element={<AluminiumPage />} />
          {/* Landing Page for Paid Traffic */}
          <Route path="/get-quote" element={<LandingPage />} />
          <Route path="/book-home-visit" element={<BookHomeVisitPage />} />
          <Route path="/request-home-visit" element={<RequestHomeVisitPage />} />
          <Route path="/free-home-visit" element={<DirectHomeVisitPage />} />
        </Routes>
      </main>
      {!isLandingPage && <Footer />}
    </>
  );
}

// Wrap App with AuthProvider
const AppWithAuth = () => (
  <AuthProvider>
    <App />
  </AuthProvider>
);

export default AppWithAuth;